import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, createQueryContext, newId } from '../engine/context'
import { projectRow, runQuery } from '../engine/directusQuery'
import { normalizeQueryParams } from '../engine/queryParams'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow, SchemaDefinition } from '../types/localDb.types'
import { immutableFieldViolation, immutableMessage } from './immutableFields'
import { runReadHook } from './readHooks'

/**
 * `/items/<collection>` dan `/items/<collection>/<id>` — emulasi Directus REST.
 * Body response mengikuti Directus: `{ data }`, error `{ errors: [{ message, extensions: { code } }] }`.
 */

const ITEMS_LIST = /^\/items\/([a-zA-Z0-9_]+)\/?$/
const ITEMS_ONE = /^\/items\/([a-zA-Z0-9_]+)\/([^/?]+)\/?$/

const parseBody = (data: unknown): unknown => {
  if (typeof data !== 'string') return data
  try {
    return JSON.parse(data)
  } catch {
    return {}
  }
}

const notFound = (collection: string, id?: string) => [
  404,
  {
    errors: [
      {
        message: id ? `Item "${id}" tidak ditemukan di ${collection}.` : `Collection ${collection} tidak ada.`,
        extensions: { code: 'FORBIDDEN' }
      }
    ]
  }
]

const forbiddenField = (collection: string, field: string) => [
  403,
  {
    errors: [
      {
        message: immutableMessage(collection, field),
        extensions: { code: 'ERR_IMMUTABLE_FIELD' }
      }
    ]
  }
]

function create(collection: string, payload: Record<string, unknown>): LocalRow {
  const row = { ...auditFieldsForCreate(), ...payload, id: String(payload.id ?? newId()) } as LocalRow
  return insertRow(collection, row)
}

export function registerItemsHandlers(mock: MockAdapter, schema: SchemaDefinition) {
  mock.onGet(ITEMS_LIST).reply(config => {
    const [, collection] = ITEMS_LIST.exec(config.url ?? '') ?? []
    runReadHook(collection)
    const ctx = createQueryContext(schema)
    return [200, runQuery(collection, readTable(collection), normalizeQueryParams(config.params), ctx)]
  })

  mock.onGet(ITEMS_ONE).reply(config => {
    const [, collection, id] = ITEMS_ONE.exec(config.url ?? '') ?? []
    runReadHook(collection)
    const row = findRow(collection, decodeURIComponent(id))
    if (!row) return notFound(collection, id)
    const ctx = createQueryContext(schema)
    return [200, { data: projectRow(collection, row, normalizeQueryParams(config.params).fields, ctx) }]
  })

  mock.onPost(ITEMS_LIST).reply(config => {
    const [, collection] = ITEMS_LIST.exec(config.url ?? '') ?? []
    const body = parseBody(config.data)
    const ctx = createQueryContext(schema)
    if (Array.isArray(body)) {
      const rows = body.map(item => create(collection, item as Record<string, unknown>))
      return [200, { data: rows.map(row => projectRow(collection, row, config.params?.fields, ctx)) }]
    }
    const row = create(collection, (body ?? {}) as Record<string, unknown>)
    return [200, { data: projectRow(collection, row, config.params?.fields, ctx) }]
  })

  mock.onPatch(ITEMS_ONE).reply(config => {
    const [, collection, id] = ITEMS_ONE.exec(config.url ?? '') ?? []
    const body = (parseBody(config.data) ?? {}) as Record<string, unknown>
    const locked = immutableFieldViolation(collection, body)
    if (locked) return forbiddenField(collection, locked)
    const updated = updateRow(collection, decodeURIComponent(id), { ...body, ...auditFieldsForUpdate() })
    if (!updated) return notFound(collection, id)
    const ctx = createQueryContext(schema)
    return [200, { data: projectRow(collection, updated, config.params?.fields, ctx) }]
  })

  /** PATCH batch Directus: body `{ keys: [], data: {} }` atau array row ber-id. */
  mock.onPatch(ITEMS_LIST).reply(config => {
    const [, collection] = ITEMS_LIST.exec(config.url ?? '') ?? []
    const body = parseBody(config.data) as
      | { keys?: string[]; data?: Record<string, unknown> }
      | Array<Record<string, unknown>>
    const ctx = createQueryContext(schema)
    const lockedField = Array.isArray(body)
      ? body.map(item => immutableFieldViolation(collection, item)).find(Boolean)
      : immutableFieldViolation(collection, body.data)
    if (lockedField) return forbiddenField(collection, lockedField)
    const updated = Array.isArray(body)
      ? body.map(item => updateRow(collection, String(item.id), { ...item, ...auditFieldsForUpdate() }))
      : (body.keys ?? []).map(key => updateRow(collection, key, { ...(body.data ?? {}), ...auditFieldsForUpdate() }))
    return [
      200,
      {
        data: updated
          .filter((row): row is LocalRow => Boolean(row))
          .map(row => projectRow(collection, row, config.params?.fields, ctx))
      }
    ]
  })

  mock.onDelete(ITEMS_ONE).reply(config => {
    const [, collection, id] = ITEMS_ONE.exec(config.url ?? '') ?? []
    return deleteRow(collection, decodeURIComponent(id)) ? [204] : notFound(collection, id)
  })
}
